import { TRPCClientError } from '@trpc/client';
import { useState } from 'react';

import { logout, signIn } from '~/client/lib/auth';
import { api } from '~/client/lib/trpc';
import { useAppStore } from '~/client/store/app/appStore';
import { useTinybaseStore } from '~/client/store/tinybase/tinybaseStore';
import { User } from '~/common/types/app';

import { useCurrentUser } from '../storeHooks';
import { useClientPinAuthInfo } from './useClientPinAuthInfo';

type Params = {
  onAuthFailure?: (isUnauthorised: boolean) => void;
};

export const useHandlePinAuth = ({ onAuthFailure = logout }: Params = {}) => {
  const {
    username: localStorageUsername,
    deleteStoredUsername,
    hashAndStorePin,
    verifyPin,
    hasPin,
  } = useClientPinAuthInfo();

  const currentUser = useCurrentUser();
  const setUser = useAppStore((state) => state.setUser);
  const setNeedsClientAuth = useAppStore((state) => state.setNeedsClientAuth);
  const userStoreCrypto = useTinybaseStore((state) => state.userStoreCrypto);
  const createUserStoreCrypto = useTinybaseStore((state) => state.createUserStoreCrypto);

  const [authenticating, setAuthenticating] = useState(false);

  const { mutateAsync: verifyUserPinAuth } = api.verifyUserPinAuth.useMutation();
  const { mutateAsync: deleteUserPin } = api.deleteUserPin.useMutation();

  const username = currentUser?.email || localStorageUsername;

  const onPinVerified = async (authedUser: User, pin: string, shouldStorePin = true) => {
    if (shouldStorePin) {
      await hashAndStorePin(pin);
    }
    if (userStoreCrypto) {
      await userStoreCrypto.generateKey(pin);
    } else {
      await createUserStoreCrypto(authedUser.id, pin);
    }
    setNeedsClientAuth(false);
  };

  const handleAuth = async (pin: string) => {
    setAuthenticating(true);
    try {
      if (!username) {
        throw Error('No username found');
      }
      const user = await verifyUserPinAuth({ username, value: pin });
      await onPinVerified(user, pin);

      setUser(user);
      return true;
    } catch (err) {
      console.error((err as Error).toString());
      const is401 = err instanceof TRPCClientError && err.data.code === 'UNAUTHORIZED';
      onAuthFailure(is401);
    }
    setAuthenticating(false);
  };

  const handleClientAuth = async (pin: string) => {
    const canAuth = !!currentUser && hasPin;

    setAuthenticating(true);
    try {
      if (!canAuth) {
        throw Error('Cannot perform client auth');
      }
      await verifyPin(pin);
      await onPinVerified(currentUser, pin, false);
      return true;
    } catch (err) {
      console.error((err as Error).toString());
      onAuthFailure(canAuth);
    }
    setAuthenticating(false);
  };

  const resetUserPin = async () => {
    if (!username) {
      throw Error('No username found');
    }
    await deleteUserPin({ username });
    deleteStoredUsername();
    await logout();
    signIn();
  };

  return {
    handleAuth,
    handleClientAuth,
    onPinVerified,
    resetUserPin,
    authenticating,
    username,
  };
};
